import { CheckCircle2, XCircle } from "lucide-react";
import { format } from "date-fns";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/ui/card";
import { Badge } from "@/shared/ui/badge";
import { ScrollArea } from "@/shared/ui/scroll-area";
import type { SupplierIncomingResult } from "../api";

interface TrendDataPoint {
  date: string;
  approved: number;
  rejected: number;
  rate: number;
}

interface PieDataPoint {
  name: string;
  value: number;
  color: string;
}

interface OverviewTabProps {
  inspections: SupplierIncomingResult[];
  trendData: TrendDataPoint[];
  pieData: PieDataPoint[];
}

export function OverviewTab({ inspections, trendData, pieData }: OverviewTabProps) {
  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-2">
        {/* Trend Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Inspection Trend</CardTitle>
            <CardDescription>Daily approved vs rejected inspections</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} className="text-muted-foreground" />
                  <YAxis tick={{ fontSize: 12 }} className="text-muted-foreground" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "var(--card)",
                      border: "1px solid var(--border)",
                      borderRadius: "8px",
                    }}
                  />
                  <Legend />
                  <Bar dataKey="approved" name="Approved" fill="var(--success)" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="rejected" name="Rejected" fill="var(--destructive)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Pie Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Status Distribution</CardTitle>
            <CardDescription>Overall inspection results</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground">
                  No inspection data
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Inspections */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Recent Inspections</CardTitle>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[300px]">
            <div className="space-y-2">
              {inspections.slice(0, 20).map((ins) => (
                <div
                  key={ins.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        ins.overall_status === "approved" ? "bg-success/20" : "bg-destructive/20"
                      }`}
                    >
                      {ins.overall_status === "approved" ? (
                        <CheckCircle2 className="w-4 h-4 text-success" />
                      ) : (
                        <XCircle className="w-4 h-4 text-destructive" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-sm">
                        {ins.material_name || "Unknown Material"}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {ins.supplier_name || "Unknown Supplier"} •{" "}
                        {ins.batch_lot_number || "No lot"}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge
                      variant={ins.document_type === "coa" ? "default" : "secondary"}
                      className="text-xs"
                    >
                      {ins.document_type.toUpperCase()}
                    </Badge>
                    <p className="text-xs text-muted-foreground mt-1">
                      {format(new Date(ins.created_at), "MMM d, HH:mm")}
                    </p>
                  </div>
                </div>
              ))}
              {inspections.length === 0 && (
                <p className="text-center text-muted-foreground py-8">No inspections found</p>
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
